import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { ViewConfig, Record } from '../types/form';

export function useListView(tableName: string, recordId?: string | number, filters?: { [key: string]: any }) {
    const queryClient = useQueryClient();

    // Fetch view configuration
    const {
        data: viewConfig,
        isLoading: configLoading
    } = useQuery<ViewConfig>({
        queryKey: ['viewConfig', tableName] as const,
        queryFn: async () => {
            const response = await api.get<ViewConfig>(`/config/${tableName}`);
            // Parse layout_config if it's a string
            if (response.data.layout_config && typeof response.data.layout_config === 'string') {
                response.data.layout_config = JSON.parse(response.data.layout_config);
            }
            return response.data;
        }
    });

    // Fetch records
    const {
        data: records,
        isLoading: recordsLoading,
        error: recordsError
    } = useQuery<Record[]>({
        queryKey: ['records', tableName, filters] as const,
        queryFn: async () => {
            let url = `/${tableName}`;
            
            // Add filters as query parameters
            if (filters && Object.keys(filters).length > 0) {
                const params = new URLSearchParams();
                Object.entries(filters).forEach(([key, value]) => {
                    if (value !== null && value !== undefined && value !== '') {
                        params.append(`filter[${key}]`, String(value));
                    }
                });
                url += `?${params.toString()}`;
            }
            
            const response = await api.get<{records: Record[], total: number} | Record[]>(url);
            const records = Array.isArray(response.data) ? response.data : response.data?.records || [];
            return records;
        },
        enabled: !!tableName
    });

    // Fetch single record if ID is provided
    const {
        data: record,
        isLoading: recordLoading,
        error: recordError
    } = useQuery<Record>({
        queryKey: ['record', tableName, recordId] as const,
        queryFn: async () => {
            const response = await api.get<Record>(`/${tableName}/${recordId}`);
            return response.data;
        },
        enabled: !!tableName && !!recordId
    });

    // Save view configuration
    const saveViewConfig = useMutation<ViewConfig, Error, ViewConfig>({
        mutationFn: async (newConfig) => {
            // The layout_config is already a JavaScript object from FormLayoutEditor
            // We need to stringify it once before sending to the server
            const configToSave = {
                ...newConfig,
                layout_config: newConfig.layout_config ? JSON.stringify(newConfig.layout_config) : null
            };

            const response = await api.put<ViewConfig>(`/config/${tableName}`, configToSave);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['viewConfig', tableName]
            });
        }
    });

    // Create new record
    const createRecord = useMutation<Record, Error, Record>({
        mutationFn: async (newRecord) => {
            const response = await api.post<Record>(`/${tableName}`, newRecord);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['records', tableName]
            });
        }
    });

    // Update existing record
    const updateRecord = useMutation<Record, Error, Record>({
        mutationFn: async (updatedRecord) => {
            const id = updatedRecord.id;
            const response = await api.put<Record>(`/${tableName}/${id}`, updatedRecord);
            return response.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: ['records', tableName]
            });
            queryClient.invalidateQueries({
                queryKey: ['record', tableName, data.id]
            });
        }
    });

    // Delete record
    const deleteRecord = useMutation<void, Error, number | string>({
        mutationFn: async (id) => {
            await api.delete(`/${tableName}/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['records', tableName]
            });
        }
    });

    return {
        viewConfig,
        records,
        record,
        isLoading: configLoading || recordsLoading || (!!recordId && recordLoading),
        error: recordsError || recordError || null,
        saveViewConfig,
        createRecord,
        updateRecord,
        deleteRecord
    };
}
